import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { EyeOff } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { isApprovalStatusEditable } from '~/domain/dicom/dicomConstants';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useRoiCommands } from '~/hooks/domain/useRoiCommands';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useRoi from '~/hooks/structure-set/useRoi';
import {
    selectedROIState,
    selectionAutoShownState,
} from '~/state/atoms/structureSet/roiAtoms';
import StructureColorPicker from './ColorPicker';
import StructureField from './StructureField';

export default function StructureProperties({
    roi,
    roiId,
    onColorPickerOpenChange,
    onSelectionInput,
}: {
    roi: RoiReadModel;
    /** Branded RoiId from the projection layer. When present, rename and
     * setColor are dispatched through useRoiCommands (new path). Falls back
     * to the legacy useRoi domain-event path when absent. */
    roiId?: RoiId;
    onColorPickerOpenChange?: (open: boolean) => void;
    onSelectionInput?: (
        roiNumber: number,
        mods: { ctrl: boolean; shift: boolean }
    ) => boolean;
}) {
    const intl = useIntl();
    const fieldAnchorRef = useRef<HTMLDivElement | null>(null);
    const {
        setName: legacySetName,
        setColor: legacySetColor,
        setVisible,
        setRoiSelected,
    } = useRoi(roi);
    const { rename, setColor: commandSetColor } = useRoiCommands();
    const selectedROI = useRecoilValue<number>(selectedROIState);
    const [wasAutoShown, setAutoShown] = useRecoilState(
        selectionAutoShownState
    );
    const pendingClickRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const ROINumber = roi.roiNumber;
    const isSelected = selectedROI === ROINumber;
    const isTarget = useDoseTargetLookup().has(roi.name);

    const structureCanBeEdited = isApprovalStatusEditable(roi.approvalStatus);

    // Use projection commands when a branded RoiId is available; fall back to
    // the legacy domain-event path otherwise (e.g. during store initialisation).
    const setName = useCallback(
        (newName: string) => {
            if (roiId !== undefined) {
                rename(roiId, newName);
            } else {
                legacySetName(newName);
            }
        },
        [roiId, rename, legacySetName]
    );

    const setColor = useCallback(
        (color: DicomColor) => {
            if (roiId !== undefined) {
                commandSetColor(roiId, color);
            } else {
                legacySetColor(color);
            }
        },
        [roiId, commandSetColor, legacySetColor]
    );

    const handleSelectROI = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            const mods = {
                ctrl: e.ctrlKey || e.metaKey,
                shift: e.shiftKey,
            };
            if (onSelectionInput?.(roi.roiNumber, mods)) {
                return;
            }
            if (e.detail > 1) {
                if (pendingClickRef.current) {
                    clearTimeout(pendingClickRef.current);
                    pendingClickRef.current = null;
                }
                return;
            }
            if (isSelected) {
                pendingClickRef.current = setTimeout(() => {
                    pendingClickRef.current = null;
                    // End ephemeral visibility on deselect; pinned stays.
                    if (wasAutoShown) {
                        setVisible(false);
                    }
                    setAutoShown(false);
                    setRoiSelected();
                }, 200);
                return;
            }

            // Step-through: hide the previous selection only if its
            // visibility was ephemeral (auto-shown / solo after Hide-all).
            if (
                wasAutoShown &&
                selectedROI !== -1 &&
                selectedROI !== roi.roiNumber
            ) {
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        selectedROI,
                        false,
                        'user'
                    )
                );
            }
            // Auto-show if hidden; mark ephemeral iff we had to auto-show.
            const willAutoShow = !roi.visible;
            if (willAutoShow) {
                setVisible(true);
            }
            setAutoShown(willAutoShow);
            setRoiSelected();
        },
        [
            isSelected,
            wasAutoShown,
            selectedROI,
            roi.visible,
            roi.roiNumber,
            setVisible,
            setRoiSelected,
            setAutoShown,
            onSelectionInput,
        ]
    );

    const handleMakeVisible = (e: React.MouseEvent) => {
        e.stopPropagation();
        setVisible(true);
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer', px: 1.5, height: 21 }}
            width="100%"
            onClick={handleSelectROI}
            onMouseDown={(e) => {
                // Suppress native text selection when shift-extending a range;
                // click-time preventDefault is too late.
                if (e.shiftKey) {
                    e.preventDefault();
                }
            }}
            data-testid="structure-properties-field"
            ref={fieldAnchorRef}
        >
            <Box mr={1} display="flex" alignItems="center" width={12}>
                {roi.visible ? (
                    <StructureColorPicker
                        roi={roi}
                        onChange={setColor}
                        fieldAnchorRef={fieldAnchorRef}
                        disabled={!structureCanBeEdited}
                        onToggleVisibility={
                            isSelected
                                ? undefined
                                : () => setVisible(!roi.visible)
                        }
                        onPickerOpenChange={onColorPickerOpenChange}
                        isTarget={isTarget}
                    />
                ) : (
                    <IconButton
                        size="small"
                        onClick={handleMakeVisible}
                        aria-label={intl.formatMessage({
                            id: 'showStructure',
                        })}
                        sx={{
                            padding: '8px',
                            margin: '-8px',
                            color: 'text.secondary',
                        }}
                    >
                        <EyeOff size={11} />
                    </IconButton>
                )}
            </Box>
            <Box flex={1} minWidth={0}>
                <StructureField
                    roi={roi}
                    onNameChange={setName}
                    isEditable={structureCanBeEdited}
                    isSelected={isSelected}
                    disabled={!roi.visible && !isSelected}
                    approvalStatus={roi.approvalStatus}
                />
            </Box>
        </Box>
    );
}
